import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { Permission } from '@bmai/shared';
import { authClient } from '../../shared/AuthApiClient';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import { SessionExpiryNotifier } from '@/shared/auth/SessionExpiryNotifier';

/** First-run onboarding state, mirrored from the auth `/api/me` payload. */
export interface OnboardingState {
    needsOnboarding: boolean;
    dismissed: boolean;
    completedAt?: string;
}

/**
 * Legal-acceptance gate state (GL-42), read from `/_auth/api/legal/status`.
 * `needsAcceptance` is true only for a tenant admin whose tenant has not yet
 * accepted the current ToS/DPA bundle; the router uses it to hard-gate first use.
 */
export interface LegalGateState {
    currentVersion: string;
    accepted: boolean;
    needsAcceptance: boolean;
}

interface AuthUser {
    id: string;
    tenantId: string;
    email: string;
    displayName: string;
    roles: string[];
    permissions: Permission[];
    onboarding?: OnboardingState;
}

interface AuthContextValue {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: AuthUser | null;
    logout: () => void;
    /** Re-fetch `/api/me` (e.g. after finishing or dismissing onboarding). */
    reloadUser: () => Promise<void>;
    /**
     * Legal-acceptance gate (GL-42). Null while loading or when the status
     * endpoint is unreachable — treated as "not gated" so an outage never locks
     * an admin out of the app.
     */
    legal: LegalGateState | null;
    /** Re-fetch the legal gate (after the admin accepts on the `/legal` screen). */
    reloadLegal: () => Promise<void>;
    hasPermission: (permission: Permission) => boolean;
    hasRole: (role: string) => boolean;
    /**
     * True if the tenant's plan enables `featureKey`. Fail-open: while features
     * are still loading or couldn't be determined, everything reads as enabled
     * (mirrors the server-side entitlement guards) so nav never wrongly hides.
     */
    hasFeature: (featureKey: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
    children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<AuthUser | null>(null);
    // null = unknown (still loading or unreachable) → hasFeature fails open.
    const [features, setFeatures] = useState<string[] | null>(null);
    const [legal, setLegal] = useState<LegalGateState | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const me = await authClient.meApi.getMe();
                if (!cancelled) setUser(me as AuthUser);
            } catch {
                if (!cancelled) setUser(null);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => SessionExpiryNotifier.subscribe(() => { setUser(null); setFeatures(null); setLegal(null); }), []);

    const reloadLegal = useCallback(async (): Promise<void> => {
        try {
            const res = await authenticatedFetch('/_auth/api/legal/status');
            if (!res.ok) { setLegal(null); return; }
            const body = (await res.json()) as LegalGateState;
            setLegal(body);
        } catch {
            setLegal(null);
        }
    }, []);

    useEffect(() => {
        if (!user) { setLegal(null); return; }
        void reloadLegal();
    }, [user, reloadLegal]);

    // Load the tenant's enabled features once signed in, for surface gating. The
    // billing endpoint returns `{ features: null }` when the tenant has no
    // entitlement on record — treated as unknown → fail-open (show everything).
    //
    // GL-28 (fail-open posture): when billing is unreachable, gating stays open
    // (features === null → hasFeature returns true) so an outage never wrongly
    // hides paid surfaces. That silent ungating is made visible here with a
    // stable console marker so browser telemetry / a synthetic check can alert on
    // it — the client-side mirror of the server guard's outage alert.
    useEffect(() => {
        if (!user) { setFeatures(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await authenticatedFetch('/_billing/api/billing/features');
                if (!res.ok) {
                    console.warn('[entitlement-source-outage] features endpoint returned', res.status, '— gating fails OPEN (all surfaces shown)');
                    return; // leave null → fail-open
                }
                const body = (await res.json()) as { features: string[] | null };
                if (!cancelled) setFeatures(Array.isArray(body.features) ? body.features : null);
            } catch (error) {
                console.warn('[entitlement-source-outage] features endpoint unreachable — gating fails OPEN (all surfaces shown)', error);
                if (!cancelled) setFeatures(null);
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const userRef = useRef(user);
    userRef.current = user;
    useEffect(() => {
        let probing = false;
        async function revalidate(): Promise<void> {
            if (probing || document.visibilityState !== 'visible' || !userRef.current) return;
            probing = true;
            try {
                await authClient.meApi.getMe();
            } catch {
                SessionExpiryNotifier.notifyExpired();
            } finally {
                probing = false;
            }
        }
        document.addEventListener('visibilitychange', revalidate);
        return () => { document.removeEventListener('visibilitychange', revalidate); };
    }, []);

    const reloadUser = useCallback(async (): Promise<void> => {
        try {
            const me = await authClient.meApi.getMe();
            setUser(me as AuthUser);
        } catch {
            setUser(null);
        }
    }, []);

    const logout = useCallback(async (): Promise<void> => {
        try { await authClient.authSessionClient.logout(); } catch { void 0; }
        window.location.href = '/';
    }, []);

    const hasPermission = useCallback(
        (permission: Permission): boolean =>
            user?.permissions.some((p) => p.action === permission.action && p.resource === permission.resource) ?? false,
        [user],
    );

    const hasRole = useCallback(
        (role: string): boolean => user?.roles.includes(role) ?? false,
        [user],
    );

    const hasFeature = useCallback(
        (featureKey: string): boolean => features === null || features.includes(featureKey),
        [features],
    );

    const isAuthenticated = !!user;

    const value = useMemo<AuthContextValue>(
        () => ({ isAuthenticated, isLoading, user, logout: () => { void logout(); }, reloadUser, legal, reloadLegal, hasPermission, hasRole, hasFeature }),
        [isAuthenticated, isLoading, user, logout, reloadUser, legal, reloadLegal, hasPermission, hasRole, hasFeature],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
    return ctx;
}
